import React, { useState, useEffect, useCallback } from 'react';
import {
  C, S, Badge, StatusBadge, FormField, ErrList, StatCard,
  PageHeader, FilterBar, DataTable, Modal, FormGrid,
  useToast, useConfirm, useCRUD,
} from '../../components/inventoryShared';
import {
  categoryController, subCategoryController, brandController,
  unitController, productController, warehouseController,
  locationController, supplierController, purchaseOrderController,
  purchaseReturnController, stockInController, stockOutController,
  stockTransferController, stockAdjustmentController,
  stockController, reportController,
} from '../../controllers/inventoryController';
import {
  Tag, Layers, Boxes, Settings, Package, BarChart3,
  PackageCheck, TrendingDown, ArrowLeftRight, ClipboardList,
  Users, ShoppingCart, RotateCcw, Warehouse, MapPin,
  AlertTriangle, Receipt, FileText, QrCode, Plus,
  Edit2, Trash2, Eye, Download, RefreshCw, CheckCircle,
  XCircle, Truck, ChevronDown, ChevronUp, Search,
} from 'lucide-react';
import makeSimplePage from './makeSimplePage';
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

// ─────────────────────────────────────────────────────────────────────────────
// 15. STOCK MANAGEMENT PAGE (aggregated view)
// ─────────────────────────────────────────────────────────────────────────────
export const StockManagementPage = ({ companyId }) => {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [dash, setDash]       = useState(null);
  const { show, ToastEl }     = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [summary, dashboard] = await Promise.all([
      stockController.getStockSummary(companyId),
      stockController.getDashboard(companyId),
    ]);
    if (summary.success) setData(summary.data);
    else show(summary.message, 'error');
    if (dashboard.success) setDash(dashboard.data);
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = data.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.sku?.toLowerCase().includes(search.toLowerCase())
  );

  const stockColor = (p) => p.currentStock <= 0 ? C.danger : p.currentStock <= p.minStock ? C.warning : C.success;
  const stockLabel = (p) => p.currentStock <= 0 ? 'Out of Stock' : p.currentStock <= p.minStock ? 'Low Stock' : 'In Stock';

  return (
    <div style={S.page}>
      {ToastEl}
      <PageHeader title="Stock Management" subtitle="Real-time inventory overview" Icon={BarChart3} color={C.primary}
        action={<button style={S.btnSec} onClick={fetchData}><RefreshCw size={15}/> Refresh</button>} />

      {/* Dashboard stats */}
      {dash && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:20 }}>
          <StatCard label="Total Products" value={dash.totalProducts||0} color={C.primary} bg='#eff6ff' Icon={Package} />
          <StatCard label="Total Value" value={money(dash.totalValue||0)} color={C.success} bg='#f0fdf4' Icon={Receipt} />
          <StatCard label="Low Stock" value={dash.lowStock||0} color={C.warning} bg='#fffbeb' Icon={AlertTriangle} />
          <StatCard label="Out of Stock" value={dash.outOfStock||0} color={C.danger} bg='#fef2f2' Icon={XCircle} />
          <StatCard label="Expiring Soon" value={dash.expiringSoon||0} color={C.orange} bg='#fff7ed' Icon={Receipt} />
        </div>
      )}

      <div style={S.card}>
        <div style={{ padding:'14px 14px 0' }}>
          <FilterBar search={search} onSearch={setSearch} />
        </div>
        {loading ? (
          <div style={S.center}><div style={{ width:32, height:32, border:`3px solid ${C.border}`, borderTop:`3px solid ${C.primary}`, borderRadius:'50%', animation:'spin .8s linear infinite', margin:'0 auto 10px'}}/><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  {['Product','SKU','Category','Warehouse','Current Stock','Min Stock','Status','Value'].map(h=><th key={h} style={S.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p,i)=>(
                  <tr key={p.id||p._id||i} onMouseEnter={e=>e.currentTarget.style.background='#f9fafb'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={S.td}><div style={{ fontWeight:700 }}>{p.name}</div></td>
                    <td style={S.td}><span style={{ fontFamily:'monospace', fontSize:12, color:C.primary }}>{p.sku||'—'}</span></td>
                    <td style={S.td}>{p.categoryName ? <Badge color={C.primary} bg={`${C.primary}12`}>{p.categoryName}</Badge> : '—'}</td>
                    <td style={S.td}>{p.warehouseName||'—'}</td>
                    <td style={S.td}><div style={{ fontWeight:800, color:stockColor(p), fontSize:15 }}>{p.currentStock}</div><div style={{ fontSize:11, color:C.textMuted }}>{p.unitName||''}</div></td>
                    <td style={S.td}>{p.minStock}</td>
                    <td style={S.td}><Badge color={stockColor(p)} bg={`${stockColor(p)}15`} dot>{stockLabel(p)}</Badge></td>
                    <td style={S.td}><span style={{ fontWeight:700 }}>{money((p.currentStock||0)*(p.purchasePrice||0))}</span></td>
                  </tr>
                ))}
                {!filtered.length && <tr><td colSpan={8} style={S.center}>No stock data found</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockManagementPage;
